import React, { useEffect, useState, useContext } from 'react';
import { AuthContext } from '../../../context/AuthContext';
import { toast } from 'react-toastify';

interface RoundTable {
    id: number;
    name: string;
    number: number;
}

interface AttachedRechnung {
    id: number;
    date: string;
}

interface ProjectEmail {
    id: number;
    projectId: number;
    roundTableId: number;
    roundTable?: RoundTable;
    Cc: string;
    subject: string;
    body: string;
    sentAt: string;
    sendingTranscript: string;
    attachedRechnungId: number | null;
    attachedRechnung?: AttachedRechnung | null;
}

interface ProjectEmailsSectionProps {
    projectId: number;
    rechnungId: number;
}

const ProjectEmailsSection: React.FC<ProjectEmailsSectionProps> = ({ projectId, rechnungId }) => {
    const authContext = useContext(AuthContext);
    const [projectEmails, setProjectEmails] = useState<ProjectEmail[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    if (!authContext) {
        throw new Error('ProjectEmailsSection must be used within an AuthProvider');
    }

    const { token } = authContext;

    useEffect(() => {
        const fetchProjectEmails = async () => {
            try {
                const response = await fetch(`/api/projects/${projectId}/emails?rechnungId=${rechnungId}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (!response.ok) {
                    throw new Error('Fehler beim Laden der Projekt-E-Mails');
                }

                const data: ProjectEmail[] = await response.json();
                setProjectEmails(data);
                setError(null);
            } catch (error) {
                console.error('Error fetching project emails:', error);
                setError(error instanceof Error ? error.message : 'Fehler beim Laden der Projekt-E-Mails');
                toast.error(error instanceof Error ? error.message : 'Fehler beim Laden der Projekt-E-Mails');
            } finally {
                setLoading(false);
            }
        };

        fetchProjectEmails();
    }, [projectId, rechnungId, token]);

    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('de-DE', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
        }).format(date);
    };

    if (loading) {
        return (
            <div className="project-emails-section">
                <h2>Projekt E-Mails</h2>
                <div className="loading">Lade E-Mails...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="project-emails-section">
                <h2>Projekt E-Mails</h2>
                <div className="error">{error}</div>
            </div>
        );
    }

    if (projectEmails.length === 0) {
        return (
            <div className="project-emails-section">
                <h2>Projekt E-Mails</h2>
                <div className="no-emails">
                    <p>Keine E-Mails zu dieser Rechnung vorhanden</p>
                </div>
            </div>
        );
    }

    return (
        <div className="project-emails-section">
            <h2>Projekt E-Mails</h2>
            <div className="project-emails-list">
                {projectEmails.map((email) => (
                    <div key={email.id} className="project-email-item">
                        <div className="email-header">
                            <div className="email-meta">
                                <span className="email-date">{formatDate(email.sentAt)}</span>
                                {email.roundTable && (
                                    <span className="email-recipient">
                                        → {email.roundTable.name} (RT {email.roundTable.number})
                                    </span>
                                )}
                            </div>
                            {email.attachedRechnungId && (
                                <span className="email-attachment-badge">📎 Mit Rechnung</span>
                            )}
                        </div>
                        <div className="email-subject">
                            <strong>Betreff:</strong> {email.subject}
                        </div>
                        {email.Cc && (
                            <div className="email-cc">
                                <strong>CC:</strong> {email.Cc}
                            </div>
                        )}
                        <div className="email-body">
                            <pre>{email.body}</pre>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ProjectEmailsSection;
